import {useCallback, useEffect, useRef} from "react";

function isTouchEvent({nativeEvent}) {
    return window.TouchEvent ? nativeEvent instanceof TouchEvent : 'touches' in nativeEvent;
}

function isMouseEvent(event) {
    return event.nativeEvent instanceof MouseEvent;
}

function getCurrentPosition(event) {
    if (isTouchEvent(event)) {
        return {
            x: event.touches[0].pageX,
            y: event.touches[0].pageY,
        };
    }
    if (isMouseEvent(event)) {
        return {
            x: event.pageX,
            y: event.pageY,
        };
    }
    return null;
}

const LongPressDetectEvents = {
    "BOTH": "both",
    "MOUSE": "mouse",
    "TOUCH": "touch"
};


/**
 * Detect click / tap and hold event
 *
 * @param callback <p>
 *   Function to call when long press event is detected
 *   (click or tap lasts for <i>threshold</i> amount of time or longer)
 *   </p>
 * @param options <ul>
 * <li><b>threshold</b>
 * - Period of time that must elapse after detecting click or tap in order to trigger <i>callback</i></li>
 * <li><b>captureEvent</b>
 * - If React Event will be supplied as first argument to all callbacks</li>
 * <li><b>detect</b>
 * - Which type of events should be detected ('mouse' | 'touch' | 'both' )
 * <li><b>cancelOnMovement</b>
 * - <p>If long press should be canceled on mouse / touch move.</p>
 * <p>You can use this option to turn it on / off or set specific move tolerance as follows:</p>
 * <ol><li><i>true</i> or <i>false</i> (by default) - when set to true tolerance value will default to <i>25px</i>
 * <li><i>number</i> - set a specific tolerance value (square size inside which movement won't cancel long press)</li></ol>
 * </li>
 * <li><b>onStart</b>
 * - Called right after detecting click / tap event (e.g. onMouseDown or onTouchStart)
 * <li><b>onFinish</b>
 * - Called (if long press <u>was triggered</u>)
 * on releasing click or tap (e.g. onMouseUp, onMouseLeave or onTouchEnd)
 * <li><b>onCancel</b>
 * - Called (if long press <u>was <b>not</b> triggered</u>)
 * on releasing click or tap (e.g. onMouseUp, onMouseLeave or onTouchEnd)
 * </ul>
 */
function useLongPress(callback, {
    threshold = 400,
    captureEvent = false,
    detect = LongPressDetectEvents.BOTH,
    cancelOnMovement = true,
    onStart,
    onMove,
    onFinish,
    onCancel,
}) {
    const isLongPressActive = useRef(false);
    const isPressed = useRef(false);
    const timer = useRef();
    const savedCallback = useRef(callback);
    const startPosition = useRef(null);

    const start = useCallback((event) => {
        // Prevent multiple start triggers
        if (isPressed.current) {
            return;
        }
        // Ignore events other than mouse and touch
        if (!isMouseEvent(event) && !isTouchEvent(event)) {
            return;
        }
        startPosition.current = getCurrentPosition(event);
        if (captureEvent) {
            event.persist();
        }
        // When touched trigger onStart and start timer
        captureEvent ? onStart?.(event) : onStart?.();
        isPressed.current = true;
        timer.current = setTimeout(() => {
            if (savedCallback.current) {
                captureEvent ? savedCallback.current(event) : savedCallback.current();
                isLongPressActive.current = true;
            }
        }, threshold);
    }, [captureEvent, onStart, threshold]);

    const cancel = useCallback((event) => {
        // Ignore events other than mouse and touch
        if (!isMouseEvent(event) && !isTouchEvent(event)) {
            return;
        }
        startPosition.current = null;
        if (captureEvent) {
            event.persist();
        }
        // Trigger onFinish callback only if timer was active
        if (isLongPressActive.current) {
            captureEvent ? onFinish?.(event) : onFinish?.();
        }
        else if (isPressed.current) {
            // Otherwise if not active trigger onCancel
            captureEvent ? onCancel?.(event) : onCancel?.();
        }
        isLongPressActive.current = false;
          isPressed.current = false;
        timer.current !== undefined && clearTimeout(timer.current);
    }, [captureEvent, onFinish, onCancel]);

    const handleMove = useCallback((event) => {
        captureEvent ? onMove?.(event) : onMove?.();
        if (cancelOnMovement && startPosition.current) {
            var currentPosition = getCurrentPosition(event);
            if (currentPosition) {
                const moveThreshold = cancelOnMovement === true ? 25 : cancelOnMovement;
                const movedDistance = {
                    x: Math.abs(currentPosition.x - startPosition.current.x),
                    y: Math.abs(currentPosition.y - startPosition.current.y),
                };
                // If moved outside move tolerance box then cancel long press
                if (movedDistance.x > moveThreshold || movedDistance.y > moveThreshold) {
                    cancel(event);
                }
            }
        }
    }, [cancel, cancelOnMovement, captureEvent, onMove]);

    useEffect(() => {
        return () => {
            // Clear timeout on unmount
            timer.current !== undefined && clearTimeout(timer.current);
        };
    }, []);

    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    const mouseHandlers = {
        onMouseDown: start,
        onMouseMove: handleMove,
        onMouseUp: cancel,
        onMouseLeave: cancel,
    };
    const touchHandlers = {
        onTouchStart: start,
        onTouchMove: handleMove,
        onTouchEnd: cancel,
    };

    if (callback === null) {
        return {};
    }
    if (detect === LongPressDetectEvents.MOUSE) {
        return mouseHandlers;
    }
    if (detect === LongPressDetectEvents.TOUCH) {
        return touchHandlers;
    }
    return {...mouseHandlers, ...touchHandlers};
}

function scrollPosition(element) {
    let x = 0, y = 0;
    while (element) {
        x += element.scrollLeft;
        y += element.scrollTop;
        element = element.parentElement;
    }
    return {x, y};
}

function closeEnough(pos1, pos2) {
    return Math.abs(pos1.x - pos2.x) + Math.abs(pos1.y - pos2.y) < 5;
}

/**
 * Returns props that, when spliced into an element's
 * cause onShort to be called on a short press/click, and
 * cause onLong to be called on a long press/click.
 * Nothing happens if the finger/mouse leaves the element
 * before being released.
 * @param onShort
 * @param onLong
 * @param options
 * @returns {{}}
 */
const usePress = (onShort, onLong, options={threshold: 300}) => {
    const timeStampRef = useRef(0);
    const startTimestampRef = useRef(0);
    const scrollPosRef = useRef(null);
    const onStart = useCallback((event) => {
        if (timeStampRef.current === event.timeStamp) {
            return;
        }
        startTimestampRef.current = event.timestamp;
        scrollPosRef.current = scrollPosition(event.target);
    }, [scrollPosRef]);
    const callback = useCallback((event) => {
        if (timeStampRef.current === event.timeStamp) {
            return; // duplicate touch/mouse event
        }
        timeStampRef.current = event.timeStamp;
        if (closeEnough(scrollPosRef.current, scrollPosition(event.target))) {
            typeof onLong === 'function' && onLong(event);
        }
    }, [scrollPosRef, onLong]);
    const onCancel = useCallback((event) => {
        if (!startTimestampRef.current || event.timeStamp === startTimestampRef.current) {
            return; // spurious mousedown/mouseup pair on Brave/responsive
        }
        if (!timeStampRef.current || event.timeStamp === timeStampRef.current) {
            return; // duplicate touch/mouse event
        }
        startTimestampRef.current = 0;
        timeStampRef.current = event.timeStamp;
        if (closeEnough(scrollPosRef.current, scrollPosition(event.target))) {
            typeof onShort === 'function' && onShort(event);
        }
    }, [scrollPosRef, onShort]);
    return useLongPress(callback, {
        ...options,
        captureEvent: true,
        onStart,
        onCancel,
    });
};

export default usePress;
